/**
 * PrizeDrawStyle1 (Awards Ceremony) section renderers for GridEngine
 * Gold/black premium theme with twinkling stars and starburst animations
 */
import React from 'react'
import Star from 'lucide-react/dist/esm/icons/star.js';
import Trophy from 'lucide-react/dist/esm/icons/trophy.js';
import Award from 'lucide-react/dist/esm/icons/award.js';
import Ticket from 'lucide-react/dist/esm/icons/ticket.js';
import Calendar from 'lucide-react/dist/esm/icons/calendar.js';
import Clock from 'lucide-react/dist/esm/icons/clock.js';
import Sparkles from 'lucide-react/dist/esm/icons/sparkles.js';
import InlineTranslationEdit from '../../../components/InlineTranslationEdit'
import CountdownTimerStyles from '../../../components/CountdownTimerStyles'
import { useContent } from '../../../contexts/ContentContext'
import { useTranslations } from '../../../contexts/TranslationContext'
import { usePurchaseNavigate } from '../../../hooks/usePurchaseNavigate'
import HeroPrizeSpotlight from '../../../components/sections/HeroPrizeSpotlight'
import { getHeroPrize } from '../../../utils/prizeHelpers'

/**
 * Floating twinkling star helper component
 */
const FloatingStar = ({ className, delay = '0s', size = 'md' }) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-10 h-10'
  }

  return (
    <div
      className={`hidden sm:block motion-reduce:!animate-none absolute ${className}`}
      style={{
        animation: `twinkle 3s ease-in-out infinite`,
        animationDelay: delay
      }}
    >
      <Star className={`${sizeClasses[size]} text-amber-400 fill-amber-400`} />
    </div>
  )
}

/**
 * Hero Section - Gold/black awards ceremony theme with twinkling stars
 */
export function PrizeDrawStyle1HeroSection({ 
  mainDraw, 
  partner, 
  editMode, 
  handleTranslationUpdate, 
  onLoginClick,
  prizes,
  timeLeft,
  shouldAnimate,
  countdownStyle
}) {
  const { c } = useContent()
  const { t: tr } = useTranslations()
  const purchaseNavigate = usePurchaseNavigate(onLoginClick)
  const heroPrize = getHeroPrize(prizes || mainDraw?.prizes || [])

  const rawPrizes = prizes || mainDraw?.prizes || []
  const firstPrizeTier = rawPrizes.find(p => p.tier === 'first')
  const secondPrizeTier = rawPrizes.find(p => p.tier === 'second')
  const thirdPrizeTier = rawPrizes.find(p => p.tier === 'third')
  const grandPrizeTier = rawPrizes.find(p => p.tier === 'grand')
  const grandPrize = grandPrizeTier?.value ?? firstPrizeTier?.value ?? mainDraw?.current_jackpot ?? 10000

  const drawDateLabel = mainDraw?.draw_date
    ? new Date(mainDraw.draw_date).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    : ''

  return (
    <div className="overflow-hidden bg-gradient-to-br from-amber-500 via-yellow-600 to-black">
      <style>{`
        @keyframes twinkle {
          0%, 100% { opacity: 0.3; transform: scale(1) rotate(0deg); }
          50% { opacity: 1; transform: scale(1.2) rotate(180deg); }
        }
        @keyframes gold-glow {
          0%, 100% { 
            text-shadow: 0 0 20px rgba(251, 191, 36, 0.8), 
                         0 0 40px rgba(251, 191, 36, 0.6),
                         0 0 60px rgba(251, 191, 36, 0.4);
            filter: drop-shadow(0 0 10px rgba(251, 191, 36, 0.6));
          }
          50% { 
            text-shadow: 0 0 30px rgba(251, 191, 36, 1), 
                         0 0 60px rgba(251, 191, 36, 0.8),
                         0 0 90px rgba(251, 191, 36, 0.6);
            filter: drop-shadow(0 0 20px rgba(251, 191, 36, 0.8));
          }
        }
        @keyframes starburst {
          0% { transform: scale(0.95) rotate(0deg); opacity: 0.8; }
          50% { transform: scale(1.05) rotate(180deg); opacity: 1; }
          100% { transform: scale(0.95) rotate(360deg); opacity: 0.8; }
        }
        .gold-glow {
          animation: gold-glow 2.5s ease-in-out infinite;
        }
        .starburst {
          animation: starburst 6s linear infinite;
        }
      `}</style>

      {/* Twinkling Stars Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <FloatingStar className="top-[8%] left-[12%]" delay="0s" size="sm" />
        {!heroPrize && <FloatingStar className="top-[15%] right-[18%]" delay="0.5s" size="md" />}
        {heroPrize && <FloatingStar className="top-[10%] right-[3%]" delay="0.5s" size="md" />}
        <FloatingStar className="top-[45%] left-[8%]" delay="1s" size="lg" />
        <FloatingStar className={heroPrize ? 'bottom-[10%] right-[3%]' : 'bottom-[25%] right-[15%]'} delay="1.5s" size="md" />
        <FloatingStar className="bottom-[40%] left-[20%]" delay="2s" size="sm" />
        {!heroPrize && <FloatingStar className="top-[60%] right-[25%]" delay="2.5s" size="sm" />}
        
        {/* Radial spotlight effect */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-amber-400/20 rounded-full blur-3xl" />
      </div>

      {/* Main Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className={heroPrize ? 'md:grid md:grid-cols-2 md:items-start md:gap-10 space-y-6 md:space-y-0' : 'text-center space-y-6'}>
          
          {/* Hero Text Content */}
          <div className={heroPrize ? 'space-y-6' : 'space-y-6'}>
            {/* Partner Logo */}
            {partner?.partner_logo_url && (
              <div className={`flex ${heroPrize ? 'justify-center md:justify-start' : 'justify-center'}`}>
                <img
                  src={partner.partner_logo_url}
                  alt={partner.partner_name || 'Partner'}
                  className="h-16 md:h-20 object-contain bg-white/90 rounded-lg p-2"
                />
              </div>
            )}

            {/* Awards Ceremony Badge */}
            <div className={`inline-flex items-center gap-3 px-6 py-3 bg-black/60 border-2 border-amber-400 rounded-full backdrop-blur-sm ${heroPrize ? '' : ''}`}>
              <Award className="w-5 h-5 text-amber-300" />
              <span className="text-amber-300 font-black text-base uppercase tracking-widest">
                <InlineTranslationEdit
                  translationKey="prizeDraw.awards_badge"
                  value={c('prizeDraw.awards_badge', '🏆 Awards Ceremony')}
                  fallback="🏆 Awards Ceremony"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </span>
              <Award className="w-5 h-5 text-amber-300" />
            </div>

            {/* Stacked Title */}
            <div className="space-y-3">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-amber-300 gold-glow leading-none">
                <InlineTranslationEdit
                  translationKey="prizeDraw.poster_title_line1"
                  value={c('prizeDraw.poster_title_line1', 'PRIZE')}
                  fallback="PRIZE"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </h1>
              <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-black text-white leading-none">
                <InlineTranslationEdit
                  translationKey="prizeDraw.poster_title_line2"
                  value={c('prizeDraw.poster_title_line2', 'DRAW')}
                  fallback="DRAW"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </h2>
              <p className="text-xl md:text-2xl text-amber-200 font-semibold tracking-wide mt-4">
                <InlineTranslationEdit
                  translationKey="prizeDraw.poster_tagline"
                  value={c('prizeDraw.poster_tagline', 'WIN AMAZING PRIZES · SUPPORT A GREAT CAUSE')}
                  fallback="WIN AMAZING PRIZES · SUPPORT A GREAT CAUSE"
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </p>
            </div>

            <HeroPartnerGlassSelect className={`w-full max-w-md ${heroPrize ? 'mx-auto md:mx-0' : 'mx-auto'}`} />

            {/* Grand Prize in Spotlight with Starburst - hidden when hero spotlight image is present (redundant) */}
            {!heroPrize && (
            <div className="relative py-4">
              {/* Starburst Effect Behind */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 starburst opacity-20">
                <div className="w-full h-full relative">
                  {[...Array(12)].map((_, i) => (
                    <div
                      key={i}
                      className="absolute top-1/2 left-1/2 w-2 h-48 bg-gradient-to-t from-amber-400/0 to-amber-400"
                      style={{
                        transform: `translate(-50%, -50%) rotate(${i * 30}deg)`,
                        transformOrigin: 'center'
                      }}
                    />
                  ))}
                </div>
              </div>

              <div className="relative">
                <div className="text-amber-300 text-lg md:text-xl font-bold uppercase tracking-widest mb-4 flex items-center justify-center gap-2">
                  <Trophy className="w-8 h-8" />
                  <InlineTranslationEdit
                    translationKey="prizeDraw.grand_prize_label"
                    value={c('prizeDraw.grand_prize_label', 'Grand Prize')}
                    fallback="Grand Prize"
                    onUpdate={handleTranslationUpdate}
                    editMode={editMode}
                  />
                  <Trophy className="w-8 h-8" />
                </div>
                <div className="relative inline-block">
                  <div className="absolute inset-0 bg-amber-400/30 blur-3xl rounded-full" />
                  <div className="relative text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-black text-amber-300 gold-glow leading-none">
                    ${grandPrize.toLocaleString()}
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Prize Tiers - Podium Style */}
            <div className={`flex items-end gap-4 ${heroPrize ? 'md:justify-start justify-center md:mx-0 md:max-w-none max-w-3xl mx-auto' : 'justify-center max-w-3xl mx-auto'}`}>
              {/* 2nd Place */}
              <div className={`flex-1 bg-gradient-to-b from-gray-300 to-gray-500 rounded-t-xl text-center border-2 border-gray-400 flex flex-col justify-between ${heroPrize ? 'p-4 h-44' : 'p-6 h-48'}`}>
                <div className="inline-flex items-center justify-center w-12 h-12 bg-gray-200 rounded-full mx-auto mb-2">
                  <Award className="w-8 h-8 text-gray-600" />
                </div>
                <div>
                  <div className="text-2xl font-black text-white mb-1">{tr('prizeDraw.secondPlace', '2ND')}</div>
                  <div className="text-xl font-bold text-gray-100">
                    {secondPrizeTier?.value != null
                      ? `$${Number(secondPrizeTier.value).toLocaleString()}`
                      : c('prizeDraw.second_prize', '$2,500')}
                  </div>
                  {secondPrizeTier?.description && (
                    <div className="text-xs text-gray-200 mt-1 line-clamp-2">{secondPrizeTier.description}</div>
                  )}
                </div>
              </div>

              {/* 1st Place (Tallest) */}
              <div className={`flex-1 bg-gradient-to-b from-amber-300 to-amber-600 rounded-t-xl text-center border-2 border-amber-400 flex flex-col justify-between shadow-2xl shadow-amber-500/50 ${heroPrize ? 'p-4 h-56' : 'p-6 h-64'}`}>
                <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-200 rounded-full mx-auto mb-2">
                  <Trophy className="w-12 h-12 text-amber-700" />
                </div>
                <div>
                  <div className="text-3xl font-black text-amber-900 mb-1">{tr('prizeDraw.firstPlace', '1ST')}</div>
                  <div className="text-2xl font-black text-amber-950">
                    ${(firstPrizeTier?.value ?? grandPrize).toLocaleString()}
                  </div>
                  {firstPrizeTier?.description && (
                    <div className="text-xs text-amber-800 mt-1 line-clamp-2">{firstPrizeTier.description}</div>
                  )}
                </div>
              </div>

              {/* 3rd Place */}
              <div className={`flex-1 bg-gradient-to-b from-orange-300 to-orange-600 rounded-t-xl text-center border-2 border-orange-400 flex flex-col justify-between ${heroPrize ? 'p-4 h-36' : 'p-6 h-40'}`}>
                <div className="inline-flex items-center justify-center w-10 h-10 bg-orange-200 rounded-full mx-auto mb-2">
                  <Award className="w-6 h-6 text-orange-700" />
                </div>
                <div>
                  <div className="text-xl font-black text-white mb-1">{tr('prizeDraw.thirdPlace', '3RD')}</div>
                  <div className="text-lg font-bold text-orange-100">
                    {thirdPrizeTier?.value != null
                      ? `$${Number(thirdPrizeTier.value).toLocaleString()}`
                      : c('prizeDraw.third_prize', '$1,000')}
                  </div>
                  {thirdPrizeTier?.description && (
                    <div className="text-xs text-orange-200 mt-1 line-clamp-2">{thirdPrizeTier.description}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Draw Date — left column only when no spotlight */}
            {!heroPrize && mainDraw?.draw_date && (
              <div className="bg-black/60 backdrop-blur-sm border-2 border-amber-400/50 rounded-2xl px-8 py-6 inline-block">
                <div className="flex items-center gap-3 text-amber-300">
                  <Calendar className="w-6 h-6" />
                  <div className="text-left">
                    <div className="text-xs uppercase tracking-wide text-amber-400/80">
                      <InlineTranslationEdit
                        translationKey="prizeDraw.draw_date_label"
                        value={c('prizeDraw.draw_date_label', 'Draw Date')}
                        fallback="Draw Date"
                        onUpdate={handleTranslationUpdate}
                        editMode={editMode}
                      />
                    </div>
                    <div className="text-2xl font-bold text-white">{drawDateLabel}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Countdown — left column only when no spotlight */}
            {!heroPrize && mainDraw?.draw_date && timeLeft && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 text-amber-300 justify-center">
                  <Clock className="w-5 h-5" />
                  <span className="text-lg font-bold uppercase tracking-wider">
                    <InlineTranslationEdit
                      translationKey="prizeDraw.time_until_draw"
                      value={c('prizeDraw.time_until_draw', 'Time Until Draw')}
                      fallback="Time Until Draw"
                      onUpdate={handleTranslationUpdate}
                      editMode={editMode}
                    />
                  </span>
                </div>
                <CountdownTimerStyles
                  timeLeft={timeLeft}
                  shouldAnimate={shouldAnimate}
                  style={countdownStyle}
                />
              </div>
            )}

            {/* CTA - Enter To Win — left column only when no spotlight */}
            {!heroPrize && (
              <div className="pt-4">
                <button
                  onClick={() => purchaseNavigate('/purchase')}
                  className="group relative inline-flex items-center gap-3 px-12 py-5 bg-gradient-to-r from-amber-400 to-amber-600 hover:from-amber-300 hover:to-amber-500 text-black font-black text-xl rounded-full shadow-2xl shadow-amber-500/50 transition-all duration-300 hover:scale-105 hover:shadow-amber-500/70"
                >
                  <Ticket className="w-6 h-6" />
                  <span>
                    <InlineTranslationEdit
                      translationKey="prizeDraw.enter_to_win"
                      value={c('prizeDraw.enter_to_win', 'Enter To Win')}
                      fallback="Enter To Win"
                      onUpdate={handleTranslationUpdate}
                      editMode={editMode}
                    />
                  </span>
                  <Sparkles className="w-6 h-6 group-hover:rotate-12 transition-transform" />
                </button>
              </div>
            )}

            {/* CTA - Enter To Win — left column, below podium, when spotlight is present */}
            {heroPrize && (
              <div className="pt-2 flex justify-center">
                <button
                  onClick={() => purchaseNavigate('/purchase')}
                  className="group relative inline-flex w-full sm:w-auto items-center justify-center gap-3 px-10 py-4 bg-gradient-to-r from-amber-400 to-amber-600 hover:from-amber-300 hover:to-amber-500 text-black font-black text-lg rounded-full shadow-2xl shadow-amber-500/50 transition-all duration-300 hover:scale-105 hover:shadow-amber-500/70"
                >
                  <Ticket className="w-6 h-6" />
                  <span>
                    <InlineTranslationEdit
                      translationKey="prizeDraw.enter_to_win"
                      value={c('prizeDraw.enter_to_win', 'Enter To Win')}
                      fallback="Enter To Win"
                      onUpdate={handleTranslationUpdate}
                      editMode={editMode}
                    />
                  </span>
                  <Sparkles className="w-6 h-6 group-hover:rotate-12 transition-transform" />
                </button>
              </div>
            )}

          </div>

          {/* Hero Prize Spotlight Column — absorbs Draw Date / Countdown / CTA below the fold to balance the grid */}
          {heroPrize && (
            <div className="flex flex-col gap-6 md:pt-8">
              <div className="flex items-start justify-center">
                <HeroPrizeSpotlight prize={heroPrize} theme="style1" />
              </div>

              {/* Draw Date + Countdown side-by-side */}
              {mainDraw?.draw_date && (
                <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-4 items-stretch">
                  {/* Draw Date card (compact — matched density to countdown digit grid) */}
                  <div className="bg-black/60 backdrop-blur-sm border-2 border-amber-400/50 rounded-2xl px-4 py-3 flex items-center gap-2.5">
                    <Calendar className="w-5 h-5 text-amber-300 shrink-0" />
                    <div className="text-left leading-none">
                      <div className="text-[10px] uppercase tracking-wide text-amber-400/80 leading-tight">
                        <InlineTranslationEdit
                          translationKey="prizeDraw.draw_date_label"
                          value={c('prizeDraw.draw_date_label', 'Draw Date')}
                          fallback="Draw Date"
                          onUpdate={handleTranslationUpdate}
                          editMode={editMode}
                        />
                      </div>
                      <div className="text-base font-bold text-white leading-tight mt-0.5">{drawDateLabel}</div>
                    </div>
                  </div>

                  {/* Countdown column */}
                  {timeLeft && (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 text-amber-300">
                        <Clock className="w-4 h-4" />
                        <span className="text-xs font-bold uppercase tracking-wider">
                          <InlineTranslationEdit
                            translationKey="prizeDraw.time_until_draw"
                            value={c('prizeDraw.time_until_draw', 'Time Until Draw')}
                            fallback="Time Until Draw"
                            onUpdate={handleTranslationUpdate}
                            editMode={editMode}
                          />
                        </span>
                      </div>
                      <CountdownTimerStyles
                        timeLeft={timeLeft}
                        shouldAnimate={shouldAnimate}
                        style={countdownStyle}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * How It Works Section - 3-step guide with awards badge
 */
export function PrizeDrawStyle1HowItWorksSection({ editMode, handleTranslationUpdate }) {
  const { c } = useContent()

  const steps = [
    { icon: Ticket, titleKey: 'prizeDraw.step1Title', titleDefault: 'Buy Your Tickets', descKey: 'prizeDraw.step1Desc', descDefault: 'Purchase your entries online securely' },
    { icon: Clock, titleKey: 'prizeDraw.step2Title', titleDefault: 'Wait for the Draw', descKey: 'prizeDraw.step2Desc', descDefault: 'Winners drawn on the announced date' },
    { icon: Trophy, titleKey: 'prizeDraw.step3Title', titleDefault: 'Claim Your Prize', descKey: 'prizeDraw.step3Desc', descDefault: 'Winners contacted and prizes awarded' }
  ]

  return (
    <div className="bg-gradient-to-br from-gray-900 to-black text-white rounded-2xl shadow-2xl border-2 border-amber-500/30 p-8">
      <div className="flex items-center justify-center gap-3 mb-8">
        <Award className="w-6 h-6 text-amber-400" />
        <h3 className="text-2xl font-black text-amber-300 uppercase tracking-wider">
          <InlineTranslationEdit
            translationKey="prizeDraw.howItWorks"
            value={c('prizeDraw.howItWorks', 'How It Works')}
            fallback="How It Works"
            onUpdate={handleTranslationUpdate}
            editMode={editMode}
          />
        </h3>
        <Award className="w-6 h-6 text-amber-400" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {steps.map((step, index) => {
          const Icon = step.icon
          return (
            <div key={index} className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center shadow-lg shadow-amber-500/50">
                <Icon className="w-8 h-8 text-black" />
              </div>
              <h4 className="text-xl font-bold text-amber-300 mb-2">
                <InlineTranslationEdit
                  translationKey={step.titleKey}
                  value={c(step.titleKey, step.titleDefault)}
                  fallback={step.titleDefault}
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </h4>
              <p className="text-sm text-gray-300">
                <InlineTranslationEdit
                  translationKey={step.descKey}
                  value={c(step.descKey, step.descDefault)}
                  fallback={step.descDefault}
                  onUpdate={handleTranslationUpdate}
                  editMode={editMode}
                />
              </p>
            </div>
          )
        })}
      </div>
    </div>
  )
}
